import { CANVAS_WIDTH } from "../../game/constants";
import type { Rect, UiRects } from "../../game/input";
import { isMobileViewport } from "../../game/platform";
import { computeProfileTotalPages, PROFILE_ITEMS_PER_PAGE } from "../../game/profilePagination";
import type { ProfileVisibleItem } from "../../game/profileVisibleItems";
import { RARITY_COLORS } from "../../economy/characters";
import { easeOutCubic } from "../../shared/easing";
import type { RenderModel } from "../renderModel";
import {
  GAME_FONT,
  MENU_TEXT_PRIMARY,
  uiCloseSize,
  UI_CLOSE_Y,
  UI_EDGE_PAD,
  menuGap,
  mFontPlain,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface ProfileGridDeps {
  ctx: CanvasRenderingContext2D;
  nowMs: () => number;
  drawCircleCloseButton: (rect: Rect) => void;
  drawCirclePencilButton: (rect: Rect) => void;
  drawCircleArrowButton: (rect: Rect, dir: -1 | 1) => void;
  drawCharacterPortrait: (
    path: string, x: number, y: number, w: number, h: number,
  ) => void;
  drawProfileFilterTabs: (
    ctx: CanvasRenderingContext2D,
    filter: "all" | "wallet",
    hasWallet: boolean,
    curY: number,
    rowX: number,
    rowW: number,
    uiRects: UiRects,
  ) => void;
  staggerAlpha: (index: number, delayMs: number, durationMs: number) => number;
  /** Mutable ref: timestamp of last equip flash for white flash overlay. */
  lastEquipFlashMs: number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawProfileGridScreen(
  deps: ProfileGridDeps,
  model: RenderModel,
  profileItems: ProfileVisibleItem[],
  uiRects: UiRects,
  filter: "all" | "wallet",
): UiRects {
  const {
    ctx, nowMs,
    drawCircleCloseButton, drawCirclePencilButton, drawCircleArrowButton,
    drawCharacterPortrait, drawProfileFilterTabs, staggerAlpha,
  } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const hasWallet = model.walletAddress != null;

  // Close button top-right corner (standard position)
  const closeRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCircleCloseButton(closeRect);
  uiRects.profileBack = closeRect;

  // Customise icon next to close button
  const customiseRect: Rect = { x: closeRect.x - uiCloseSize() - 6, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCirclePencilButton(customiseRect);
  uiRects.profileEditSkin = customiseRect;

  // "SKINS" title + filter tabs
  const rowW = CANVAS_WIDTH - UI_EDGE_PAD * 2;
  const rowX = UI_EDGE_PAD;
  const tabH = isMobileViewport() ? 22 : 18;
  const tabY = 120;

  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 10px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("SKINS", rowX, tabY + tabH / 2);

  drawProfileFilterTabs(ctx, filter, hasWallet, tabY, rowX, rowW, uiRects);

  const page = model.profilePage;
  const perPage = PROFILE_ITEMS_PER_PAGE;
  const totalPages = computeProfileTotalPages(profileItems.length);
  const pageStart = page * perPage;
  const pageEnd = Math.min(pageStart + perPage, profileItems.length);
  const pageItems = profileItems.slice(pageStart, pageEnd);

  // Character grid (4x3 square cards)
  const cols = 4;
  const maxRows = 3;
  const gapX = menuGap();
  const gapY = menuGap();
  const gridW = CANVAS_WIDTH - UI_EDGE_PAD * 2;
  const cardW = Math.floor((gridW - gapX * (cols - 1)) / cols);
  const gridStartX = UI_EDGE_PAD;
  const gridStartY = tabY + tabH + 8;

  // Derive the actual logical canvas height from the physical canvas size.
  // The canvas is scaled by DPR; recover it from the known logical width.
  const dpr = ctx.canvas.width / CANVAS_WIDTH;
  const logicalH = ctx.canvas.height / dpr;
  // 16 = MENU_CONTENT_OFFSET_PX applied externally via ctx.translate
  const contentOffset = 16;
  const arrowSize = 24;
  const footerPad = gapY;
  const availableH = logicalH - contentOffset - gridStartY - arrowSize - footerPad * 2;
  const maxCardH = Math.floor((availableH - gapY * (maxRows - 1)) / maxRows);
  const cardH = Math.min(cardW, maxCardH);

  for (let i = 0; i < pageItems.length; i++) {
    const item = pageItems[i];
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = gridStartX + col * (cardW + gapX);
    const cy = gridStartY + row * (cardH + gapY);
    const rarityColor = (RARITY_COLORS as Record<string, string>)[item.rarity] ?? "#3a4870";

    const gridAlpha = staggerAlpha(i, 60, 250);
    if (gridAlpha <= 0) continue;
    if (gridAlpha < 1) {
      ctx.save();
      const scl = 0.8 + 0.2 * easeOutCubic(gridAlpha);
      const cardCX = cx + cardW / 2;
      const cardCY = cy + cardH / 2;
      ctx.globalAlpha = easeOutCubic(gridAlpha);
      ctx.translate(cardCX, cardCY);
      ctx.scale(scl, scl);
      ctx.translate(-cardCX, -cardCY);
    }

    roundedRectPath(ctx, cx, cy, cardW, cardH, 6);
    const rarityBg: Record<string, string> = {
      COMMON: "#2a2e34",
      RARE: "#1a3050",
      EPIC: "#2e1a48",
    };
    const nftBg = "#1a1a38";
    ctx.fillStyle = item.kind === "nft" ? nftBg : (rarityBg[item.rarity] ?? "#141e32");
    ctx.globalAlpha = gridAlpha < 1 ? easeOutCubic(gridAlpha) : 1;
    ctx.fill();
    // Clip portrait to card bounds
    ctx.save();
    roundedRectPath(ctx, cx, cy, cardW, cardH, 6);
    ctx.clip();
    drawCharacterPortrait(item.imagePath, cx, cy, cardW, cardH);
    ctx.restore();

    // Border on top of clipped portrait
    roundedRectPath(ctx, cx, cy, cardW, cardH, 6);
    ctx.strokeStyle = item.equipped ? "#ffd700" : (item.kind === "nft" ? "#7060ff" : rarityColor);
    ctx.lineWidth = item.equipped ? 2.5 : 1;
    ctx.stroke();
    ctx.lineWidth = 1;

    if (item.equipped && deps.lastEquipFlashMs > 0) {
      const equipFlashElapsed = nowMs() - deps.lastEquipFlashMs;
      if (equipFlashElapsed < 300) {
        const flashA = 0.5 * (1 - equipFlashElapsed / 300);
        ctx.save();
        roundedRectPath(ctx, cx, cy, cardW, cardH, 6);
        ctx.fillStyle = `rgba(255,255,255,${flashA.toFixed(3)})`;
        ctx.fill();
        ctx.restore();
      }
    }

    if (item.kind === "nft" && item.source === "delegated") {
      const badgeR = 10;
      const badgeCX = cx + 4 + badgeR;
      const badgeCY = cy + 4 + badgeR;
      ctx.save();
      ctx.beginPath();
      ctx.arc(badgeCX, badgeCY, badgeR, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0,0,0,0.65)";
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold 12px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("D", badgeCX, badgeCY);
      ctx.restore();
    }

    if (gridAlpha < 1) ctx.restore();

    if (!item.equipped) {
      const cardRect: Rect = { x: cx, y: cy, w: cardW, h: cardH };
      if (item.kind === "nft") {
        const nftKey = `avatarSelectNft${i}` as keyof UiRects;
        (uiRects as Record<string, Rect>)[nftKey] = cardRect;
      } else {
        const key = `profileEquip${i}` as keyof UiRects;
        (uiRects as Record<string, Rect>)[key] = cardRect;
      }
    }
  }

  if (filter === "wallet" && profileItems.length === 0) {
    ctx.fillStyle = "#8eb6d9";
    ctx.font = mFontPlain(5);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const emptyMsg = hasWallet ? "NO NFTS FOUND" : "CONNECT WALLET ON PHONE";
    ctx.fillText(emptyMsg, centerX, gridStartY + cardH);
  }

  // Footer: circle arrow buttons (same as shop)
  const actualRows = Math.min(maxRows, Math.ceil(pageItems.length / cols)) || maxRows;
  const footerY = gridStartY + actualRows * (cardH + gapY) + (arrowSize / 2);
  const prevRect: Rect = { x: UI_EDGE_PAD, y: footerY, w: arrowSize, h: arrowSize };
  const nextRect: Rect = { x: CANVAS_WIDTH - arrowSize - UI_EDGE_PAD, y: footerY, w: arrowSize, h: arrowSize };

  if (page > 0) {
    drawCircleArrowButton(prevRect, -1);
    uiRects.profilePagePrev = prevRect;
  }

  if (page < totalPages - 1) {
    drawCircleArrowButton(nextRect, 1);
    uiRects.profilePageNext = nextRect;
  }

  return uiRects;
}
